<template>
  <Html :lang="head.htmlAttrs.lang" :dir="head.htmlAttrs.dir">
    <Head>
      <template v-for="meta in head.meta">
        <Meta
        :property="meta.property"
        :content="meta.content"
        />
      </template>
    </Head>
    <Body>
      <div class="focus:outline-[0px]" ref="refocus" tabindex="-1">
        
        <Header v-if="!hideHeader" />
        <slot />
        <Footer v-if="!hideFooter" />
        <GetQuoteButton v-if="!hideHeader" />

        <ClientOnly>
          <CountrySelect v-if="countryModal && userCountry" />
        </ClientOnly>

      </div>
    </Body>
  </Html>
</template>

<script setup>
useHead({
  script: [
    {
      src: 'https://assets.calendly.com/assets/external/widget.js',
      async: true
    },
  ]
})

const route = useRoute()
const head = useLocaleHead()
const userStore = useUserStore()

// Campaign pages (pages/go/) can hide the site chrome via these flags; they
// wrap this layout explicitly so their setup runs first and the flags are set
// before the layout serialises on the server.
const hideHeader = useState('hideHeader', () => false)
const hideFooter = useState('hideFooter', () => false)
const refocus = ref()
const currentUrl = ref('')
const { countryModal, countrySelected, userCountry } = storeToRefs(userStore)
userStore.countryModal = false

onMounted(() => {
  if (process.client && !userStore.heroTest) {
    const result = Math.random() > 0.5 ? 'a' : 'b'
    userStore.setHeroTest(result)
  }
})

const demoContactAdded = ref(false)

const generateRandomString = () => {
  let result = ''
  let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let charactersLength = characters.length
  for (var i = 0; i < 30; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
  }
  return result
}

// generate an anonymous ID for our user
if (!userStore.userId) {
  const userId = generateRandomString()
  userStore.storeUserId(userId)
}

// Return document.referrer only when it is a real external origin. Same-origin
// (elker.com -> elker.com) referrers are internal navigations, not acquisition
// sources, so they return null and never become the first-touch referrer.
const externalReferrer = () => {
  if (!process.client) return null
  // Named referrerUrl, not `ref`: a local called `ref` makes Nuxt skip
  // auto-importing Vue's ref() for this whole file, breaking the client build.
  const referrerUrl = document?.referrer || null
  if (!referrerUrl) return null
  try {
    if (new URL(referrerUrl).origin === window.location.origin) return null
  } catch (e) {
    return null // unparseable referrer -> treat as none
  }
  return referrerUrl
}

const getSourceAndMedium = referrer => {
  const sources = [
    { domain: 'linkedin.', source: 'linkedin', medium: 'social' },
    { domain: 'google.', source: 'google', medium: 'organic' },
    { domain: 'bing.', source: 'bing', medium: 'organic' },
    { domain: 'yahoo.', source: 'yahoo', medium: 'organic' },
    { domain: 'duckduckgo.', source: 'duckduckgo', medium: 'organic' },
    { domain: 'yandex.', source: 'yandex', medium: 'organic' },
    { domain: 'reddit.', source: 'reddit', medium: 'social' },
    { domain: 'quora.', source: 'quora', medium: 'social' },
    { domain: 'facebook.', source: 'facebook', medium: 'social' },
    { domain: 'twitter.', source: 'twitter', medium: 'social' },
    { domain: 'x.com', source: 'twitter', medium: 'social' },
    { domain: 'perplexity.ai', source: 'perplexity', medium: 'organic' },
    { domain: 'msn.com', source: 'msn', medium: 'organic' },
    { domain: 'ecosia.org', source: 'ecosia', medium: 'organic' }
  ]

  if (!referrer) {
    return { source: 'direct', medium: 'none' }
  }

  const matchedSource = sources.find(item => referrer.includes(item.domain));
  if (matchedSource) {
    return { source: matchedSource.source, medium: matchedSource.medium };
  }

  return { source: referrer, medium: 'referral' };
}


const checkRouteParams = async () => {

  // stop the form firing twice
  if (demoContactAdded.value) return

  const userEmail = route?.query?.invitee_email || null
  const userName = route?.query?.invitee_full_name || null

  if (!userEmail || !userName) {
    return
  }

  const data = {
    name: userName,
    email: userEmail,
    utmSource: userStore.utmSource,
    utmMedium: userStore.utmMedium,
    utmCampaign: userStore.utmCampaign,
    utmLandingPage: userStore.utmLandingPage,
    // Conversion attribution for server-side conversion firing.
    ...useConversionFields()
  }

  demoContactAdded.value = true

  try {
    const addUserRes = await $fetch('/api/elkerBookDemo', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
        
    if (!addUserRes.ok) {
      return
    }

  } catch(e) {
    console.log(e)
  }

  if (process.client && window.dataLayer) {

    if (userEmail) {
      window.dataLayer.push({
        event: 'elker_conversion',
        'enhanced_conversion_email': userEmail
      })
    } else {
      window.dataLayer.push({
        event: 'elker_conversion'
      })
    }

    window.dataLayer.push({
      event: 'demo_booked'
    })    
  }
}

const initView = async () => {
  if (process.client) {
    checkRouteParams()
  }
}


const trk = async () => {

  // tracking
  let utmSource = route?.query?.utm_source || null
  let utmMedium = route?.query?.utm_medium || null
  let utmCampaign = route?.query?.utm_campaign || null
  let utmTerm = route?.query?.utm_term || null
  let utmContent = route?.query?.utm_content || null
  let utmLandingPage = route?.path || null

  // Record the first-touch landing timestamp (once) for the CRM's first_seen_at,
  // and the original EXTERNAL referrer (once) for the CRM's referrer field. Only
  // store a referrer from a different origin: document.referrer on an internal
  // navigation is a same-origin elker.com URL, which is not an acquisition
  // source and must not be recorded as first-touch.
  userStore.setFirstSeen()
  userStore.setReferrer(externalReferrer())

  // Capture paid click ids + UTMs into the store BEFORE checkRouteParams(),
  // because on a Calendly-return landing checkRouteParams() fires the demo
  // booking and reads these via useConversionFields(); if it ran first the
  // booking would submit with a stale/empty gclid and lose paid attribution.
  // First-touch is kept (setClickIds only overwrites when a new id is present).
  const gclid = route?.query?.gclid || null
  const msclkid = route?.query?.msclkid || null
  if (gclid || msclkid) userStore.setClickIds(gclid, msclkid)

  if (utmSource === 'linkedin.com') utmSource = 'linkedin'

  if (utmMedium === 'cpc') utmMedium = 'paid'

  if (utmSource && utmMedium && !userStore.utmMedium) {
    userStore.updateUtm(utmSource, utmLandingPage, utmMedium, utmCampaign, utmTerm, utmContent)
  } else if (utmSource && !userStore.utmSource) {
    userStore.updateUtm(utmSource, utmLandingPage, utmMedium, utmCampaign, utmTerm, utmContent)
  }

  // Now that click ids + UTMs are in the store, the demo-booking callback can
  // read correct attribution.
  checkRouteParams()

  if (!userStore.utmSource) {
    utmSource = document?.referrer || null
    const sourceMedium = getSourceAndMedium(utmSource)
    userStore.updateUtm(sourceMedium.source, utmLandingPage, sourceMedium.medium)
  }

  initView()

}

const showCountrySelector = async () => {
  let userCountry
  if (window.location?.host && window.location?.host.includes('localhost:3000')) {
    userCountry = 'AU'
  } else {
    userCountry = await $fetch('/api/getUserCountry')
  }

  // set the country in the store
  userStore.setUserCountry(userCountry)

  if (userCountry && (userCountry === 'AU' || userCountry === 'NZ')) {
    if ((userCountry === 'AU' && route.path.startsWith('/nz')) || (userCountry === 'NZ' && !route.path.startsWith('/nz'))) {
      userStore.openCountryModal()
    }
  }
}


watch(
  () => route.path, 
  () => {
    currentUrl.value = route.fullPath
    refocus.value?.focus()
  }
)

watch(
  () => route.query, 
  () => {
    checkRouteParams()
  }
)

onMounted(async () => {
  refocus.value?.focus()

  if (process.client) trk()

  if (!countrySelected.value && process.client) {
    await showCountrySelector()
  }

})

</script>